import { Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ToastService } from '../../../core/services/toast.service';
import { VendorsService } from '../../../core/services/vendors.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { authActions } from '../../../core/store/auth';
import { ApiError } from '../../../core/models/common/api-error';
import { FormFieldsetCardComponent } from '../../../shared/components/form-fieldset-card/form-fieldset-card.component';
import { User } from '../../../core/models/entities/user';
import { ClipboardService } from 'ngx-clipboard';
import { environment } from '../../../../environments/environment';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';

@Component({
  selector: 'app-vendor-url-settings',
  template: `
    <app-fieldset-card
      [showSaveButton]="true"
      [saving]="saving()"
      [trackFormChanges]="form"
      (onSave)="save()"
    >
      <div title>Lien de partage</div>
      <div description>
        Partagez ce lien pour que vos clients puissent réserver en ligne.
      </div>

      <app-text-input
        [control]="form.controls.slug"
        name="slug"
        [prepend]="host + '/louer-salle/'"
        [appendAction]="{
          icon: 'copy',
          title: 'Copier le lien',
          action: copyLink.bind(this),
        }"
      ></app-text-input>
    </app-fieldset-card>
  `,
  imports: [SharedModule, SharedManagerModule],
})
export class VendorUrlSettingsComponent implements OnInit {
  @Input() authUser!: User;

  @ViewChild(FormFieldsetCardComponent)
  fieldsetCard: FormFieldsetCardComponent;

  private store = inject(Store);
  private toastService = inject(ToastService);
  private vendorsService = inject(VendorsService);
  private formValidationService = inject(FormValidationService);
  private clipboardService = inject(ClipboardService);

  saving = signal<boolean>(false);

  form = new FormGroup({
    slug: new FormControl<string>('', Validators.required),
    name: new FormControl<string>('', Validators.required), // Nécessaire pour l'API
  });

  get host() {
    return new URL(environment.appUrl).host;
  }

  ngOnInit() {
    this.form.patchValue({
      slug: this.authUser.vendor.slug || '',
      name: this.authUser.vendor.name || '',
    });
  }

  async save() {
    if (!this.formValidationService.validate(this.form)) return;

    this.saving.set(true);
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.value.name,
        slug: this.form.value.slug,
      });
      this.updateUserStore(vendor);
      this.toastService.success('Lien de partage enregistré.');

      // Réinitialiser le dirty state
      this.fieldsetCard?.resetDirtyState();
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    } finally {
      this.saving.set(false);
    }
  }

  copyLink() {
    this.clipboardService.copy(
      `${environment.appUrl}/louer-salle/${this.authUser.vendor.slug}`,
    );
    this.toastService.success('Lien copié dans le presse-papiers');
  }

  private updateUserStore(vendor: any) {
    this.store.dispatch(
      authActions.findCurrentUserSuccess({
        user: {
          ...this.authUser,
          vendor: { ...this.authUser.vendor, ...vendor },
        },
      }),
    );
  }
}
